// Sportello della portineria.
//
// Una pagina sola, pensata per il portiere: cerca il condòmino, vede quanto deve,
// registra quello che incassa (contanti, POS, copia del bonifico) e rivede quello che
// ha registrato. Quello che registra lui resta «da confermare» finché non lo vede
// l'amministratore: è il backend a deciderlo, non questa pagina.
//
// La stessa pagina la apre anche l'amministratore, che qui vede il pulsante «Conferma».
//
// 13/10/2026: la finestra «Registra incasso» e' un calendario. Tutti i mesi della posizione
// stanno in vista (quelli gia' pagati in verde, non toccabili), quelli aperti si cliccano uno
// accanto all'altro e il totale da incassare e' la somma dei residui cliccati.
// I comproprietari (es. A6: Giliberto e Di Stefano) sono una posizione sola: lo decide il backend.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Banknote, Search, RefreshCw, CheckCircle, Clock, Receipt, CreditCard, Landmark, Coins } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODALITA = [
  { value: 'CONTANTI', label: 'Contanti', icon: Coins },
  { value: 'POS', label: 'POS / bancomat', icon: CreditCard },
  { value: 'BONIFICO', label: 'Bonifico (copia consegnata)', icon: Landmark },
  { value: 'ASSEGNO', label: 'Assegno', icon: Receipt },
  { value: 'ALTRO', label: 'Altro', icon: Banknote },
];

const MESI_BREVI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const arrotonda = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Anni e mesi della posizione. Con `quote_mesi` (tutti i mesi dovuti) si vedono anche i pagati;
// con un backend che non lo manda ancora si ricade sulle sole quote aperte.
const costruisciCalendario = (posizione) => {
  const sorgente = (posizione?.quote_mesi && posizione.quote_mesi.length)
    ? posizione.quote_mesi : (posizione?.quote_aperte || []);
  const anni = new Map();
  sorgente.forEach((q) => {
    if (!q.anno || !q.mese) return;
    if (!anni.has(q.anno)) anni.set(q.anno, new Map());
    const mesi = anni.get(q.anno);
    const m = mesi.get(q.mese) || { anno: q.anno, mese: q.mese, dovuto: 0, incassato: 0, residuo: 0, aperte: [] };
    m.dovuto += Number(q.dovuto) || 0;
    m.incassato += Number(q.incassato) || 0;
    m.residuo += Number(q.residuo) || 0;
    if ((Number(q.residuo) || 0) > 0.004) m.aperte.push(q.quote_id);
    mesi.set(q.mese, m);
  });
  return [...anni.entries()].sort((a, b) => a[0] - b[0]).map(([anno, mesi]) => ({
    anno,
    mesi: [...mesi.values()].map((m) => ({ ...m, dovuto: arrotonda(m.dovuto), incassato: arrotonda(m.incassato), residuo: arrotonda(m.residuo) })),
  }));
};

const euro = (n) => `${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const oggiISO = () => new Date().toLocaleDateString('sv-SE');   // AAAA-MM-GG in ora locale

export const Portineria = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const condoName = selectedCondominium?.condominium?.name || 'Condominio';

  const [posizioni, setPosizioni] = useState([]);
  const [totali, setTotali] = useState({ totale_residuo: 0, totale_da_confermare: 0 });
  const [puoConfermare, setPuoConfermare] = useState(false);
  const [cerca, setCerca] = useState('');
  const [caricamento, setCaricamento] = useState(true);
  const [incassi, setIncassi] = useState([]);
  const [totaleGiornata, setTotaleGiornata] = useState(0);
  const [giorno, setGiorno] = useState(oggiISO());

  // Saldi di apertura anno (riporto dei consuntivi): + a credito, - a debito. Solo ADMIN e commercialista.
  const [saldiAperti, setSaldiAperti] = useState(null);
  const apriSaldi = async () => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/portineria/posizioni?solo_aperte=false`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Non è stato possibile leggere i saldi di apertura');
      const dati = await res.json();
      const righe = [];
      (dati.posizioni || []).forEach((p) => (p.saldi_apertura || []).forEach((v) => righe.push({ ...v, unit_number: p.unit_number, intestatario: p.intestatario })));
      righe.sort((x, y) => (x.anno - y.anno) || String(x.unit_number).localeCompare(String(y.unit_number), 'it', { numeric: true }));
      // Riepilogo PER ANNO (mai sommato fra anni diversi): addebiti e accrediti in valore assoluto e saldo NETTO = accrediti − addebiti
      const perAnno = {};
      righe.forEach((x) => {
        const a = (perAnno[x.anno] = perAnno[x.anno] || { anno: x.anno, pdfAdd: 0, pdfAcc: 0, appAdd: 0, appAcc: 0 });
        if (x.consuntivo !== undefined) {
          if (x.consuntivo < 0) a.pdfAdd += -x.consuntivo; else a.pdfAcc += x.consuntivo;
        }
        if (x.importo < 0) a.appAdd += -x.importo; else a.appAcc += x.importo;
      });
      const anni = Object.values(perAnno).sort((p, q) => p.anno - q.anno);
      setSaldiAperti({ righe, anni, nonAbbinati: dati.saldi_apertura_non_abbinati || [] });
    } catch (e) {
      toast.error(e.message);
    }
  };
  const euroSegno = (n) => `${Number(n) > 0 ? '+' : ''}${euro(n)}`;

  const [dialogo, setDialogo] = useState(null);      // la posizione su cui si sta incassando
  const [inCorso, setInCorso] = useState(false);
  // Niente `amount`: allo sportello l'importo non si scrive, si spuntano le mensilità.
  const [modulo, setModulo] = useState({ quote_ids: [], payment_date: oggiISO(), payment_method: 'POS', notes: '' });

  const caricaPosizioni = useCallback(async () => {
    if (!token || !condoId) return;
    setCaricamento(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/portineria/posizioni?q=${encodeURIComponent(cerca)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Non è stato possibile leggere le posizioni');
      const dati = await res.json();
      setPosizioni(dati.posizioni || []);
      setTotali({ totale_residuo: dati.totale_residuo, totale_da_confermare: dati.totale_da_confermare });
      setPuoConfermare(!!dati.puo_confermare);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCaricamento(false);
    }
  }, [token, condoId, cerca]);

  const caricaIncassi = useCallback(async () => {
    if (!token || !condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/portineria/incassi?dal=${giorno}&al=${giorno}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const dati = await res.json();
      setIncassi(dati.incassi || []);
      setTotaleGiornata(dati.totale || 0);
    } catch (e) {
      // la giornata è un di più: se non arriva, la pagina resta usabile
    }
  }, [token, condoId, giorno]);

  useEffect(() => { caricaPosizioni(); }, [caricaPosizioni]);
  useEffect(() => { caricaIncassi(); }, [caricaIncassi]);

  const apriIncasso = (posizione) => {
    setDialogo(posizione);
    // Nessuna mensilità preselezionata: la sceglie chi sta allo sportello, spuntandola.
    setModulo({
      quote_ids: [],
      payment_date: oggiISO(),
      payment_method: 'POS',
      notes: '',
    });
  };

  // Allo sportello si chiudono quote INTERE: l'importo non è un campo, è la somma dei
  // residui di quello che è stato spuntato. Il divieto vero sta nella rotta — questa è
  // solo la schermata che lo rispetta.
  const importoSpuntato = Math.round(
    (dialogo?.quote_aperte || [])
      .filter((q) => modulo.quote_ids.includes(q.quote_id))
      .reduce((s, q) => s + (Number(q.residuo) || 0), 0) * 100) / 100;

  const spunta = (quoteId, acceso) => setModulo((m) => ({
    ...m,
    quote_ids: acceso ? [...m.quote_ids, quoteId]
                      : m.quote_ids.filter((x) => x !== quoteId),
  }));

  // Un click su un mese spunta (o toglie) tutte le sue quote aperte: una sola per chi ha
  // un'unita' sola, piu' d'una per chi ne ha piu' d'una nello stesso mese.
  const spuntaMese = (mese) => setModulo((m) => {
    const tutte = mese.aperte.every((id) => m.quote_ids.includes(id));
    return { ...m, quote_ids: tutte ? m.quote_ids.filter((id) => !mese.aperte.includes(id))
                                    : [...new Set([...m.quote_ids, ...mese.aperte])] };
  });

  const spuntaAnno = (mesiAnno) => setModulo((m) => {
    const ids = mesiAnno.flatMap((x) => x.aperte);
    if (!ids.length) return m;
    const tutte = ids.every((id) => m.quote_ids.includes(id));
    return { ...m, quote_ids: tutte ? m.quote_ids.filter((id) => !ids.includes(id)) : [...new Set([...m.quote_ids, ...ids])] };
  });

  const spuntaTutte = () => setModulo((m) => ({
    ...m,
    quote_ids: (dialogo?.quote_aperte || []).map((q) => q.quote_id),
  }));

  const registra = async () => {
    if (!modulo.quote_ids.length) { toast.error('Spunta le mensilità che stai chiudendo'); return; }
    const importo = importoSpuntato;
    if (!(importo > 0)) { toast.error('Le mensilità spuntate non hanno residuo'); return; }
    setInCorso(true);
    try {
      // Sempre la stessa strada: l'elenco delle quote da chiudere, e l'importo che ne
      // discende. Il ramo «singola rata con importo libero» non esiste più.
      const url = `${API_URL}/api/condominiums/${condoId}/portineria/incasso-multiplo`;
      const corpo = {
        quote_ids: modulo.quote_ids,
        unit_id: dialogo?.unit_id,
        person_id: dialogo?.person_id || null,
        amount: importo,
        payment_date: modulo.payment_date,
        payment_method: modulo.payment_method,
        notes: modulo.notes || null,
      };
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const dati = await res.json();
      if (!res.ok) throw new Error(typeof dati.detail === 'string' ? dati.detail : 'Registrazione non riuscita');
      toast.success(dati.messaggio || 'Incasso registrato');
      setDialogo(null);
      await caricaPosizioni();
      await caricaIncassi();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setInCorso(false);
    }
  };

  const calendario = costruisciCalendario(dialogo);

  const conferma = async (riga) => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quotes/${riga.quote_id}/payments/${riga.payment_id}/conferma`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Conferma non riuscita');
      toast.success('Incasso confermato');
      await caricaIncassi();
      await caricaPosizioni();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Portineria"
        subtitle={`${condoName} — incassi allo sportello`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Ancora da incassare</CardDescription></CardHeader>
          <CardContent><p className="text-2xl font-semibold">{euro(totali.totale_residuo)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Incassato il {giorno.split('-').reverse().join('/')}</CardDescription></CardHeader>
          <CardContent><p className="text-2xl font-semibold">{euro(totaleGiornata)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>In attesa di conferma</CardDescription></CardHeader>
          <CardContent><p className="text-2xl font-semibold">{euro(totali.totale_da_confermare)}</p></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Chi deve ancora pagare</CardTitle>
          <CardDescription>Cerca per unità o per cognome, poi registra quello che ti ha lasciato.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input className="pl-9" placeholder="A12, oppure Bonci…" value={cerca}
                     onChange={(e) => setCerca(e.target.value)} />
            </div>
            <Button variant="outline" onClick={caricaPosizioni} disabled={caricamento}>
              <RefreshCw className={`h-4 w-4 ${caricamento ? 'animate-spin' : ''}`} />
            </Button>
            {puoConfermare && (
              <Button variant="outline" onClick={apriSaldi}>Saldi di apertura</Button>
            )}
          </div>

          {caricamento ? (
            <p className="text-sm text-muted-foreground py-6">Sto leggendo le posizioni…</p>
          ) : posizioni.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">
              {cerca ? 'Nessuno corrisponde a quello che hai scritto.' : 'Non risulta nessuna posizione aperta.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unità</TableHead>
                    <TableHead>Intestatario</TableHead>
                    {puoConfermare && <TableHead>Saldi di apertura anno</TableHead>}
                    <TableHead className="text-right">Residuo</TableHead>
                    <TableHead className="text-right">Mensilità aperte</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posizioni.map((p) => (
                    <TableRow key={`${p.unit_id}-${p.person_id || ''}`}>
                      <TableCell className="font-medium">{p.unit_number}</TableCell>
                      <TableCell>
                        {p.intestatario}
                        {(p.note_rosse || []).length > 0 && (
                          <span className="ml-1 font-bold text-red-600" title={p.note_rosse.join(' · ')}>*</span>
                        )}
                        {(p.note_rosse || []).length > 0 && (
                          <div className="text-xs font-normal text-red-600">* {p.note_rosse.join(' · ')}</div>
                        )}
                        {p.da_confermare > 0 && (
                          <Badge className="ml-2 bg-amber-100 text-amber-800 font-normal">
                            {euro(p.da_confermare)} da confermare
                          </Badge>
                        )}
                      </TableCell>
                      {puoConfermare && (
                        <TableCell className="text-xs">
                          {(p.saldi_apertura || []).map((s, i) => (
                            <span key={`${s.anno}-${i}`} title={s.consuntivo === undefined ? 'Calcolato dalle quote e dagli incassi' : `Da consuntivo ${euroSegno(s.consuntivo)} · scarto ${euroSegno(s.scarto)}`}
                                  className={`mr-3 whitespace-nowrap ${s.importo < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                              {s.anno}: {euroSegno(s.importo)}
                            </span>
                          ))}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-semibold">{euro(p.residuo)}</TableCell>
                      <TableCell className="text-right">{p.quote_aperte?.length || 0}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => apriIncasso(p)}>Registra incasso</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Registrati in giornata</CardTitle>
          <CardDescription>Quello che risulta incassato nel giorno scelto.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs">
            <Label htmlFor="giorno">Giorno</Label>
            <Input id="giorno" type="date" value={giorno} onChange={(e) => setGiorno(e.target.value)} />
          </div>
          {incassi.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun incasso registrato in questo giorno.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unità</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Modalità</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Registrato da</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incassi.map((r) => (
                    <TableRow key={r.payment_id}>
                      <TableCell className="font-medium">{r.unita}</TableCell>
                      <TableCell className="text-right">{euro(r.importo)}</TableCell>
                      <TableCell>{MODALITA.find((m) => m.value === r.modalita)?.label || r.modalita}</TableCell>
                      <TableCell>
                        {r.stato === 'DA_CONFERMARE' ? (
                          <Badge className="bg-amber-100 text-amber-800 font-normal">
                            <Clock className="h-3 w-3 mr-1" />da confermare
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 font-normal">
                            <CheckCircle className="h-3 w-3 mr-1" />confermato
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.registrato_da}</TableCell>
                      <TableCell className="text-right">
                        {puoConfermare && r.stato === 'DA_CONFERMARE' && (
                          <Button size="sm" variant="outline" onClick={() => conferma(r)}>Conferma</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!saldiAperti} onOpenChange={(v) => !v && setSaldiAperti(null)}>
        <DialogContent className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Saldi di apertura anno</DialogTitle>
            <DialogDescription>
              Saldo di apertura di ogni anno. «Saldo corretto (PDF)» è quello stampato dagli esercizi ordinari approvati: ADDEBITO = il condomino deve, ACCREDITO = il condomino è a credito.
              Il PDF 2023 non riporta gli anni precedenti: l'apertura 2023 è 0,00 per tutti (le chiusure 2022 stanno nel PDF 2022). Il 2025 è la chiusura 2024.
              «App» è calcolato dalle quote e dagli incassi caricati, per competenza (si aggiorna da solo; l'app non può registrare crediti oltre il dovuto). Lo scarto mostra dove mancano quote o versamenti.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border p-2">
            <p className="mb-1 text-xs font-medium">Riepilogo per anno: il saldo netto è la differenza fra accrediti e addebiti (negativo = a debito).</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Anno</TableHead>
                  <TableHead className="text-right">PDF: addebiti</TableHead>
                  <TableHead className="text-right">PDF: accrediti</TableHead>
                  <TableHead className="text-right">PDF: saldo netto</TableHead>
                  <TableHead className="text-right">App: addebiti</TableHead>
                  <TableHead className="text-right">App: accrediti</TableHead>
                  <TableHead className="text-right">App: saldo netto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(saldiAperti?.anni || []).map((a) => (
                  <TableRow key={`riep-${a.anno}`}>
                    <TableCell className="font-medium">{a.anno}</TableCell>
                    <TableCell className="text-right text-red-700">{a.pdfAdd || a.pdfAcc ? euro(a.pdfAdd) : '—'}</TableCell>
                    <TableCell className="text-right text-emerald-700">{a.pdfAdd || a.pdfAcc ? euro(a.pdfAcc) : '—'}</TableCell>
                    <TableCell className="text-right font-semibold">{a.pdfAdd || a.pdfAcc ? euroSegno(a.pdfAcc - a.pdfAdd) : '—'}</TableCell>
                    <TableCell className="text-right text-red-700">{euro(a.appAdd)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{euro(a.appAcc)}</TableCell>
                    <TableCell className="text-right font-semibold">{euroSegno(a.appAcc - a.appAdd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Anno</TableHead>
                <TableHead>Unità</TableHead>
                <TableHead>Intestatario</TableHead>
                <TableHead className="text-right">PDF: addebito</TableHead>
                <TableHead className="text-right">PDF: accredito</TableHead>
                <TableHead className="text-right">App: addebito</TableHead>
                <TableHead className="text-right">App: accredito</TableHead>
                <TableHead className="text-right">Scarto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(saldiAperti?.righe || []).map((s, i) => {
                const pdf = s.consuntivo;                // undefined dove nessun PDF riporta il saldo (es. 2026)
                const addebito = (n) => (n !== undefined && n < -0.004 ? euro(-n) : '—');
                const accredito = (n) => (n !== undefined && n > 0.004 ? euro(n) : '—');
                return (
                  <TableRow key={`${s.anno}-${s.unit_number}-${i}`}>
                    <TableCell>{s.anno}</TableCell>
                    <TableCell className="font-medium">{s.unit_number}</TableCell>
                    <TableCell>{s.intestatario}</TableCell>
                    <TableCell className="text-right font-semibold text-red-700">{addebito(pdf)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{accredito(pdf)}</TableCell>
                    <TableCell className="text-right text-red-700">{addebito(s.importo)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{accredito(s.importo)}</TableCell>
                    <TableCell className="text-right">{s.scarto === undefined ? '—' : (Math.abs(s.scarto) < 0.05 ? 'ok' : euroSegno(s.scarto))}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {(saldiAperti?.nonAbbinati || []).length > 0 && (
            <p className="text-xs text-muted-foreground">
              Non abbinati a una posizione: {saldiAperti.nonAbbinati.map((n) => `${n.anno} ${n.unit_number} ${n.condomino} ${euroSegno(n.importo)}`).join(' · ')}
            </p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialogo} onOpenChange={(v) => !v && setDialogo(null)}>
        <DialogContent className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registra incasso — {dialogo?.unit_number}</DialogTitle>
            <DialogDescription>
              {dialogo?.intestatario} · residuo {euro(dialogo?.residuo)}
              {(dialogo?.note_rosse || []).length > 0 && (
                <span className="block text-red-600">* {dialogo.note_rosse.join(' · ')}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">Quali mensilità sta chiudendo</span>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-green-300 bg-green-100" />già pagato</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-slate-300 bg-white" />da incassare</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-amber-300 bg-amber-50" />parziale</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-primary" />scelto</span>
                  <Button variant="outline" size="sm" onClick={spuntaTutte}>
                    Tutte le aperte ({(dialogo?.quote_aperte || []).length})
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {calendario.map((riga) => {
                  const aperteAnno = riga.mesi.flatMap((x) => x.aperte);
                  return (
                    <div key={riga.anno} className="grid grid-cols-[4.5rem_1fr] gap-2 items-start">
                      <div className="pt-1">
                        <p className="text-sm font-semibold">{riga.anno}</p>
                        {aperteAnno.length > 0 ? (
                          <button type="button" className="text-xs text-primary underline underline-offset-2"
                                  onClick={() => spuntaAnno(riga.mesi)}>
                            tutto l'anno
                          </button>
                        ) : (
                          <span className="text-xs text-green-700">saldato</span>
                        )}
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
                        {MESI_BREVI.map((nome, i) => {
                          const m = riga.mesi.find((x) => x.mese === i + 1);
                          if (!m) {
                            return (
                              <div key={nome} className="rounded-md border border-dashed bg-slate-50 px-1 py-1.5 text-center text-xs text-slate-300">
                                <span className="block font-medium">{nome}</span>
                                <span className="block">–</span>
                              </div>
                            );
                          }
                          const pagato = m.aperte.length === 0;
                          const scelto = !pagato && m.aperte.every((id) => modulo.quote_ids.includes(id));
                          const parziale = !pagato && m.incassato > 0.004;
                          const classi = pagato
                            ? 'border-green-300 bg-green-100 text-green-900 cursor-default'
                            : scelto
                              ? 'border-primary bg-primary text-primary-foreground ring-2 ring-primary/30'
                              : parziale
                                ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                                : 'border-slate-300 bg-white hover:bg-slate-50';
                          return (
                            <button key={nome} type="button" disabled={pagato} aria-pressed={scelto}
                                    onClick={() => spuntaMese(m)}
                                    title={pagato ? `${nome} ${riga.anno}: pagato (${euro(m.incassato)})`
                                                  : `${nome} ${riga.anno}: dovuto ${euro(m.dovuto)}, già incassato ${euro(m.incassato)}, residuo ${euro(m.residuo)}`}
                                    className={`rounded-md border px-1 py-1.5 text-center text-xs leading-tight transition-colors ${classi}`}>
                              <span className="block font-medium">{nome}</span>
                              <span className="block">{pagato ? '✓ pagato' : euro(m.residuo)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {calendario.length === 0 && (
                  <p className="text-sm text-muted-foreground">Questa posizione non ha mensilità da mostrare.</p>
                )}
              </div>
            </div>

            {/* L'importo non si scrive: discende da quello che è stato spuntato */}
            <div className="rounded-md bg-slate-50 p-3 flex items-center justify-between">
              <span className="text-sm">
                Totale da incassare
                <span className="block text-xs text-muted-foreground">
                  {modulo.quote_ids.length} mensilità spuntate
                </span>
              </span>
              <span className="text-xl font-bold">{euro(importoSpuntato)}</span>
            </div>

            <p className="text-xs text-muted-foreground">
              Dallo sportello si chiudono mensilità intere. Per un pagamento parziale, o
              per incassare solo l'acqua, serve l'amministratore.
            </p>

            <div>
              <Label htmlFor="data">Data</Label>
              <Input id="data" type="date" value={modulo.payment_date}
                     onChange={(e) => setModulo({ ...modulo, payment_date: e.target.value })} />
            </div>

            <div>
              <Label>Come ha pagato</Label>
              <Select value={modulo.payment_method}
                      onValueChange={(v) => setModulo({ ...modulo, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODALITA.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="note">Note</Label>
              <Textarea id="note" placeholder="Es.: copia del bonifico lasciata in portineria, CRO 12345"
                        value={modulo.notes} onChange={(e) => setModulo({ ...modulo, notes: e.target.value })} />
            </div>

            {!puoConfermare && (
              <p className="text-xs text-muted-foreground">
                Quello che registri qui resta in attesa: diventa definitivo quando l'amministratore lo conferma.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogo(null)}>Annulla</Button>
            <Button onClick={registra} disabled={inCorso || !modulo.quote_ids.length}>
              {inCorso ? 'Sto registrando…'
                       : `Chiudi ${modulo.quote_ids.length || ''} mensilità`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Portineria;
